/**
 * PdpStickyBar — desktop-only persistent utility bar (2026-07-08 reorder).
 *
 * The mobile PDP already has a sticky buy bar; desktop had NO persistent CTA
 * once the sidebar scrolled away (~15,000px of page with no way to buy). This
 * closes that gap: a slim bottom bar with the product, in-page section
 * anchors, price, and a View Deal CTA.
 *
 * Conversion contract:
 *   - The CTA is the affiliate redirect /r/{offerId}, routed through
 *     SignedOfferLink (FIX-13b) so the click carries a fresh cid/ts/hmac,
 *     with pos=sticky-desktop for per-module EPC attribution (the mobile bar
 *     in product-client.tsx is pos=sticky) — FRIDGE-276.
 *   - Renders nothing without a fresh-priced offer (mirrors the mobile gate).
 *   - z-40: below ComparisonBar (z-50), so an active comparison wins the slot.
 *   - Hidden state uses transform only (no in-flow space) → zero layout shift.
 */

'use client'

import { LearnAboutRecallLink, RetailerOfferCta } from '@/components/product/retailer-offer-cta'
import { SafeImage } from '@/components/ui/safe-image'
// FRIDGE-874 — the desktop bar carries the Save control too, so a visitor deep
// in a 15,000px page can save without scrolling back to the buy box.
import { SaveHeartButton, type SavedItem } from '@/components/chrome/save-compare-controls'

export interface PdpStickyBarAnchor {
  id: string
  label: string
}

interface PdpStickyBarProps {
  visible: boolean
  productName: string
  productId: string
  image?: string | null
  offer: { id: string; name: string; priceDisplay: string } | null
  anchors: PdpStickyBarAnchor[]
  /** FRIDGE-760 — an active CPSC recall drops the buy CTA to secondary and leads with "Learn about this recall". */
  hasActiveRecall?: boolean
  /** FRIDGE-874 — the Saved/Compare row for this product; omit to hide the heart. */
  saveItem?: SavedItem | null
}

export function PdpStickyBar({
  visible,
  productName,
  productId,
  image,
  offer,
  anchors,
  hasActiveRecall = false,
  saveItem = null,
}: PdpStickyBarProps) {
  // No monetizable CTA → no bar (same rule as the mobile sticky bar).
  if (!offer?.id) return null

  const scrollTo = (id: string) => {
    if (typeof document === 'undefined') return
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div
      className="hidden lg:flex fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] transition-transform duration-200"
      style={{ transform: visible ? 'translateY(0)' : 'translateY(100%)' }}
      aria-hidden={!visible}
      // The bar slides off-screen but stays in the DOM. Its links already
      // toggle tabIndex; the Save heart and Compare tick it gained (FRIDGE-874)
      // do not, and a focusable control inside aria-hidden is axe's
      // aria-hidden-focus (serious) — the gate flagged it on the product, part
      // and filter templates. `inert` takes the whole hidden bar out of the
      // tab order and the accessibility tree at once.
      inert={!visible}
      data-testid="sticky-buy-bar-desktop"
    >
      <div className="mx-auto max-w-[1400px] w-full px-6 xl:px-10 py-2.5 flex items-center gap-6">
        {/* Product identity */}
        <div className="flex items-center gap-3 min-w-0 flex-shrink">
          {image ? (
            <div className="w-10 h-10 flex-shrink-0 rounded bg-gray-50 overflow-hidden">
              <SafeImage
                src={image}
                alt={productName ? `${productName} | Fridge.com` : ''}
                width={40}
                height={40}
                className="w-full h-full object-contain"
              />
            </div>
          ) : null}
          <p className="text-sm font-bold text-gray-900 truncate max-w-[280px]">{productName}</p>
        </div>

        {/* In-page section anchors — only those that exist this render. */}
        {anchors.length > 0 && (
          <nav className="hidden xl:flex items-center gap-4 text-sm text-gray-600" aria-label="Jump to section">
            {anchors.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => scrollTo(a.id)}
                className="hover:text-[#0469f0] transition-colors"
                tabIndex={visible ? 0 : -1}
              >
                {a.label}
              </button>
            ))}
          </nav>
        )}

        {/* Price + CTA, pinned right */}
        <div className="flex items-center gap-4 ml-auto flex-shrink-0">
          <div className="text-right">
            <p className="text-[11px] text-gray-500 leading-none">{offer.name}</p>
            <p className="text-base font-black text-gray-900 leading-tight">{offer.priceDisplay}</p>
          </div>
          {saveItem ? <SaveHeartButton item={saveItem} /> : null}
          {hasActiveRecall && <LearnAboutRecallLink size="sm" />}
          <RetailerOfferCta
            offerId={offer.id}
            productId={productId}
            pos="sticky-desktop"
            target="_blank"
            aria-label={`View deal for ${productName} at ${offer.name}, ${offer.priceDisplay} (opens retailer site in new tab)`}
            tabIndex={visible ? 0 : -1}
            hasActiveRecall={hasActiveRecall}
            size="sm"
          >
            View Deal
          </RetailerOfferCta>
        </div>
      </div>
    </div>
  )
}

export default PdpStickyBar
